import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { FaUsers, FaSignal, FaShieldAlt, FaSignInAlt } from 'react-icons/fa';
import { MdRefresh, MdDevices } from 'react-icons/md';

import { Button, LoadingSpinner, confirmAlert } from 'components/common';
import { StatTile, BarList } from 'components/app/bookings/dashboard/DashboardBits';
import { escapeHtml } from 'helpers/utils';
import SignInTrendChart from 'components/app/authorization/sessions/SignInTrendChart';
import SessionTable from 'components/app/authorization/sessions/SessionTable';
import SessionFilterBar from 'components/app/authorization/sessions/SessionFilterBar';
import SessionLocationModal from 'components/app/authorization/sessions/SessionLocationModal';
import GeoDatabaseCard from 'components/app/authorization/sessions/GeoDatabaseCard';
import ThrottledPanel from 'components/app/authorization/sessions/ThrottledPanel';
import {
  getSessionDashboard, getSessions, getSessionFilterOptions,
  getGeoStatus, updateGeoDatabase, getThrottles, clearThrottle,
  revokeSession, revokeAllSessions, setScope, setPage, setSort,
} from 'redux/authorization/sessionDashboardSlice';

const PER_PAGE = 25;

const GRANULARITY_LABEL = {
  hour: 'hourly', day: 'daily', week: 'weekly', month: 'monthly',
};

const UserSessionsDashboardPage = () => {
  const dispatch = useDispatch();
  const {
    stats, trend, browsers, providerOptions, range: dashboardRange,
    rows, totalRows, scope, page, sortBy, sortDir, geo, throttles,
    loadingDashboard, loadingRows,
  } = useSelector((state) => state.authorization_session_dashboard);

  const [searchParams] = useSearchParams();
  const [locationSession, setLocationSession] = useState(null);
  // Seeded from the URL so the link in an alert email lands on that person's
  // sessions rather than on a page the reader then has to search.
  const [query, setQuery] = useState(() => searchParams.get('q') || '');
  const [provider, setProvider] = useState('');
  const [network, setNetwork] = useState('');
  const [throttleFilters, setThrottleFilters] = useState({
    q: '', network: '', rule: '', sort: 'recent', page: 1,
  });
  // Today, on every tab. A fortnight of sign-ins is a lot of rows to greet
  // somebody with, and the question this page is opened with is about now.
  const [range, setRange] = useState({
    startDate: dayjs().format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
  });
  // Empty means "let the server choose for this range". Cleared whenever the
  // range changes: a unit picked for one week is rarely the right one for the
  // next year, and leaving a stale choice in place makes the buttons look
  // broken when the server quietly overrides it.
  const [granularity, setGranularity] = useState('');

  const hasFilters = Boolean(query || provider || network);

  useEffect(() => {
    dispatch(getSessionFilterOptions());
    dispatch(getGeoStatus());

    const urlScope = searchParams.get('scope');
    if (urlScope && urlScope !== scope) dispatch(setScope(urlScope));
    // Mount only: after this the scope belongs to the user's clicks, not the URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // The throttle list has its own filters and pages independently of the
  // session table, so it reloads on its own rather than on every session query.
  useEffect(() => {
    dispatch(getThrottles(throttleFilters));
  }, [dispatch, throttleFilters]);

  const reload = useCallback(() => {
    dispatch(getSessionDashboard({ ...range, granularity }));
    dispatch(getSessions({
      scope, page, perPage: PER_PAGE, query, provider, network, sortBy, sortDir, ...range,
    }));
  }, [dispatch, scope, page, query, provider, network, sortBy, sortDir, range, granularity]);

  // Debounced so typing in the search box does not fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(reload, query ? 350 : 0);
    return () => clearTimeout(timer);
  }, [reload, query]);

  // Polls only while a download is running, and stops as soon as it is not —
  // a permanent interval would keep hitting the server for a status that
  // changes once a month.
  useEffect(() => {
    if (!geo?.running) return undefined;

    const timer = setInterval(() => dispatch(getGeoStatus()), 5000);
    return () => clearInterval(timer);
  }, [dispatch, geo?.running]);

  const resetFilters = () => {
    setQuery('');
    setProvider('');
    setNetwork('');
    dispatch(setPage(1));
  };

  const changeFilter = (setter) => (value) => {
    setter(value);
    dispatch(setPage(1));
  };

  const handleRevoke = (session) => {
    const who = escapeHtml(session.user?.full_name || session.user?.name || 'this user');
    const device = escapeHtml([session.device_label, session.os].filter(Boolean).join(' · '));
    const ip = escapeHtml(session.ip_address || 'unknown IP');

    confirmAlert({
      title: 'Sign this device out?',
      // Named explicitly: from a table of near-identical rows, the device and IP
      // are the only things that tell one of somebody's sessions from another.
      message: `<b>${who}</b> will be signed out on <b>${device}</b> (${ip}) the next time that device calls the API. Their other devices stay signed in.`,
      type: 'warning',
      onConfirm: async () => {
        await dispatch(revokeSession(session.id));
        reload();
      },
    });
  };

  const handleRevokeAll = (session) => {
    const who = escapeHtml(session.user?.full_name || session.user?.name || 'this user');

    confirmAlert({
      title: 'Sign out every device?',
      message: `Every session belonging to <b>${who}</b> will be signed out, including the one they may be using right now. Use this when an account is believed compromised.`,
      type: 'danger',
      onConfirm: async () => {
        await dispatch(revokeAllSessions(session.user.id));
        reload();
      },
    });
  };

  const trendSubtitle = useMemo(() => {
    const from = dayjs(range.startDate).format('D MMM');
    const to = dayjs(range.endDate).format('D MMM YYYY');
    return `${from} – ${to} · ${GRANULARITY_LABEL[trend.granularity] || trend.granularity}`;
  }, [range, trend.granularity]);

  return (
    <div className="tw-px-6 tw-pb-8">
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-py-4">
        <div className="tw-flex tw-items-center tw-gap-2.5">
          <span className="tw-inline-flex tw-items-center tw-justify-center tw-h-10 tw-w-10 tw-rounded-lg tw-bg-blue-50 tw-text-blue-600">
            <MdDevices size={20} />
          </span>
          <div>
            <h4 className="tw-text-lg tw-font-semibold tw-text-slate-800 tw-m-0">Sessions</h4>
            <p className="tw-text-xs tw-text-slate-400 tw-m-0">
              Who is signed in, from where, and on what
            </p>
          </div>
        </div>

        <Button size="sm" variant="secondary" onClick={reload} disabled={loadingDashboard || loadingRows}>
          <MdRefresh size={15} /> Reload
        </Button>
      </div>

      {/* Replaces a static "run this rake task" notice: the state and the action
          that fixes it belong in the same place, or the notice just tells
          somebody without shell access about a task they cannot run. */}
      <GeoDatabaseCard geo={geo} onUpdate={() => dispatch(updateGeoDatabase())} />

      {/* Always present, quiet when nobody is blocked. It is the only evidence
          on screen that sign-in rate limiting exists at all. */}
      <ThrottledPanel
        throttles={throttles}
        filters={throttleFilters}
        onFiltersChange={setThrottleFilters}
        onClear={async (key) => {
          await dispatch(clearThrottle(key));
          dispatch(getThrottles(throttleFilters));
        }}
      />

      <div className="tw-grid tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-3 tw-mb-4">
        {/* The first two are live regardless of the date range — the labels say
            so, because a "live" number that silently answers for last March
            would be worse than no number. */}
        <StatTile
          label="Users online now"
          value={stats.users_online}
          icon={FaUsers}
          accent="tw-text-green-600"
          bg="tw-bg-green-50"
        />
        <StatTile
          label="Active sessions now"
          value={stats.active_sessions}
          icon={FaSignal}
          accent="tw-text-blue-600"
          bg="tw-bg-blue-50"
        />
        <StatTile
          label="Sign-ins in range"
          value={stats.sign_ins}
          icon={FaSignInAlt}
          accent="tw-text-primary-600"
          bg="tw-bg-primary-50"
        />
        {/* Highlighted and clickable only when there is something to look at —
            a permanently red tile reading 0 trains people to ignore it. */}
        <StatTile
          label="Suspicious sign-ins in range"
          value={stats.suspicious}
          icon={FaShieldAlt}
          accent={stats.suspicious ? 'tw-text-red-600' : 'tw-text-slate-600'}
          bg={stats.suspicious ? 'tw-bg-red-50' : 'tw-bg-slate-100'}
          highlight={Boolean(stats.suspicious)}
          onClick={stats.suspicious ? () => dispatch(setScope('suspicious')) : undefined}
        />
      </div>

      <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-3 tw-mb-4">
        <div className="lg:tw-col-span-2">
          <SignInTrendChart
            title="Sign-ins"
            subtitle={trendSubtitle}
            series={trend.series}
            granularity={trend.granularity}
            available={dashboardRange.available_granularities || []}
            onGranularityChange={setGranularity}
          />
        </div>
        <BarList
          title="Browsers in range"
          items={browsers.map((b) => ({ label: b.label, value: b.count }))}
          emptyText="No sessions in this range"
        />
      </div>

      <SessionFilterBar
        scope={scope}
        onScopeChange={(next) => dispatch(setScope(next))}
        query={query}
        onQueryChange={changeFilter(setQuery)}
        provider={provider}
        onProviderChange={changeFilter(setProvider)}
        providerOptions={providerOptions}
        network={network}
        onNetworkChange={changeFilter(setNetwork)}
        startDate={range.startDate}
        endDate={range.endDate}
        onDateChange={(next) => {
          setRange(next);
          setGranularity('');
          dispatch(setPage(1));
        }}
        onReset={resetFilters}
        hasFilters={hasFilters}
      />

      {loadingRows && (
        <div className="tw-mb-2"><LoadingSpinner size="sm" /></div>
      )}

      <SessionTable
        rows={rows}
        totalRows={totalRows}
        page={page}
        perPage={PER_PAGE}
        loading={loadingRows}
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={(column) => dispatch(setSort(column))}
        onPageChange={(next) => dispatch(setPage(next))}
        onRevoke={handleRevoke}
        onRevokeAll={handleRevokeAll}
        onShowLocation={setLocationSession}
      />

      <SessionLocationModal
        session={locationSession}
        onClose={() => setLocationSession(null)}
        remoteEnabled={Boolean(geo?.remote_enabled)}
      />
    </div>
  );
};

export default UserSessionsDashboardPage;
